import { Heart, Building, Hammer, Trophy, Users, Quote, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import rolandsPortrait from "@/assets/rolands-portrait.webp";
import YouTubeFacade from "@/components/YouTubeFacade";

const differentiators = [
  {
    icon: Building,
    title: "Gym Owners & Operators",
    description: "We own the largest bouldering gym in the Baltics. We understand daily operations, member needs, and what makes facilities successful—because we live it every day."
  },
  {
    icon: Hammer,
    title: "Hold Manufacturers",
    description: "We design and manufacture climbing holds, mats, and equipment under brands like Emotion and OnClimb. Our competition-level expertise ensures ergonomic grips and premium quality used in facilities worldwide."
  },
  {
    icon: Trophy,
    title: "Competition Organizers",
    description: "We organize regional competitions and events. We know exactly what professional facilities need and how to create exciting climbing experiences that wow participants."
  },
  {
    icon: Users,
    title: "Community Builders",
    description: "We actively develop the climbing scene across the Baltic region. We mentor climbers, grow the sport, and create spaces where communities thrive."
  }
];

const CompetitiveEdge = () => {
  return (
    <section id="why" className="relative overflow-hidden">
      {/* Hero Section - Bold Statement */}
      <div className="py-16 bg-gradient-hero text-accent-foreground relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-10 w-72 h-72 border-2 border-primary-foreground rounded-full" />
          <div className="absolute bottom-20 left-10 w-96 h-96 border-2 border-primary-foreground transform rotate-45" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center gap-3 mb-8 px-6 py-3 bg-primary/20 backdrop-blur-sm rounded-full">
              <Heart className="w-6 h-6 text-primary fill-primary" />
              <span className="text-primary-foreground font-semibold uppercase tracking-wider">
                Built by Climbers, For Climbers
              </span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl lg:text-7xl font-display mb-6 leading-tight">
              CLIMBING WALL CONTRACTORS<br />
              WITH REAL EXPERTISE
            </h2>
            
            <p className="text-xl sm:text-2xl text-accent-foreground/90 leading-relaxed mb-4">
              Unlike other climbing gym builders, <span className="text-primary font-semibold">we live and breathe climbing</span>. 
              Our bouldering gym construction benefits from real-world experience as gym owners, climbing hold manufacturers, 
              competition organizers, and climbing community builders across Europe.
            </p>

            <div className="flex items-center justify-center gap-2 text-lg text-accent-foreground/80">
              <span>Led by ex-professional climber</span>
              <ArrowRight className="w-5 h-5" />
              <span className="text-primary font-semibold">Rolands Ruģēns</span>
            </div>
          </div>
        </div>
      </div>

      {/* Rolands Section - The Secret Weapon */}
      <div className="py-16 bg-background relative">
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-amber-600/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Hero Portrait Section */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Portrait */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:-translate-y-2 group">
                  <img 
                    src={rolandsPortrait} 
                    alt="Rolands Ruģēns - Falkors Walls owner and ex-professional climber with 16 years IFSC World Cup experience, leading climbing gym construction company"
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
                <div className="absolute -top-4 -left-4 w-32 h-32 bg-amber-600/10 rounded-full blur-2xl" />
              </div>

              {/* Content */}
              <div className="space-y-6">
                <Badge variant="outline" className="text-sm font-semibold uppercase tracking-wider">
                  Falkors Walls Owner
                </Badge>
                <h3 className="text-4xl sm:text-5xl lg:text-6xl font-display text-foreground leading-tight">
                  ROLANDS RUĢĒNS
                </h3>
                <p className="text-2xl sm:text-3xl font-display text-primary">
                  Ex-Pro Climber. Gym Builder. Hold Manufacturer.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  What truly sets Falkors apart is our leadership—<span className="text-foreground font-semibold">16 years competing at IFSC World Cups with a 4th place world finish</span>, now owner of the largest bouldering center in the Baltics and building world-class facilities. This isn't theoretical knowledge—it's battle-tested expertise no competitor can match.
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                    <Trophy className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-foreground">4th Place World</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                    <Building className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-foreground">Biggest Bouldering Center</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                    <Hammer className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-foreground">10+ Facilities Built</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-10">
            <Card className="p-6 text-center bg-card border-primary/20 hover:border-primary/40 transition-colors">
              <div className="text-4xl font-display text-primary mb-1">16</div>
              <div className="text-xs text-muted-foreground">Years at World Cups</div>
            </Card>
            <Card className="p-6 text-center bg-card border-primary/20 hover:border-primary/40 transition-colors">
              <div className="text-4xl font-display text-primary mb-1">4th</div>
              <div className="text-xs text-muted-foreground">Best World Cup Finish</div>
            </Card>
            <Card className="p-6 text-center bg-card border-primary/20 hover:border-primary/40 transition-colors">
              <div className="text-4xl font-display text-primary mb-1">#1</div>
              <div className="text-xs text-muted-foreground">Largest Gym in Baltics</div>
            </Card>
            <Card className="p-6 text-center bg-card border-primary/20 hover:border-primary/40 transition-colors">
              <div className="text-4xl font-display text-primary mb-1">3+</div>
              <div className="text-xs text-muted-foreground">Hold Brands Created</div>
            </Card>
          </div>

          {/* Personal Story */}
          <div className="max-w-4xl mx-auto mb-10">
            <Card className="p-8 md:p-12 bg-muted/50 border-primary/20 relative overflow-hidden">
              <div className="absolute top-6 left-6 opacity-10">
                <Quote className="w-16 h-16 text-primary" />
              </div>
              <div className="relative z-10 space-y-6">
                <h4 className="text-2xl sm:text-3xl font-display text-foreground mb-4">
                  From Frustrated Climber to Facility Expert
                </h4>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    "I started climbing at six, following my father Ēriks Ruģēns—a legendary urban alpinist. At seven, I began serious training. Back then, we had one wooden wall with carved holds. Same routes every day. It was mind-numbingly boring."
                  </p>
                  <p className="text-foreground font-semibold text-lg">
                    "That frustration became my motivation. If I wanted better facilities, I had to build them myself."
                  </p>
                  <p>
                    "So we built our own gym. Not just for me—for everyone who wanted to climb without limitations. During 16 years competing internationally, I saw world-class facilities and terrible ones. I learned what climbers truly need—not from books, but from thousands of hours competing in Europe, China, and across the globe."
                  </p>
                  <p>
                    "I competed at the highest level—4th at IFSC World Cup Innsbruck 2016, 6th at Kazo 2016, 8th at Meiringen 2017. I know what elite facilities require because I've climbed in them. I know what beginners need because I've taught them. I know what gym owners face because I am one."
                  </p>
                  <p className="text-foreground font-semibold text-lg border-l-4 border-primary pl-4">
                    "When I build your gym, I'm not guessing. I'm building the facility I wished existed when I started competing. That's the Falkors difference."
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Video Section */}
          <div className="max-w-5xl mx-auto mb-10">
            <div className="text-center mb-8">
              <h4 className="text-2xl sm:text-3xl font-display text-foreground mb-3">
                Hear It Directly From Rolands
              </h4>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Learn how we build and manage world-class climbing facilities from someone who has competed at the highest level and now operates the largest bouldering gym in the Baltics.
              </p>
            </div>
            
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-muted/50 backdrop-blur-sm border-2 border-primary/10 hover:border-primary/20 transition-colors">
              <div className="aspect-video">
                <YouTubeFacade
                  videoId="P-4b9s-3RDA"
                  title="Climbing Gym Construction Process - Professional Bouldering Gym Builder Rolands Falkors Explains Turnkey Facility Development"
                />
              </div>
            </div>
          </div>

          {/* Four Pillars of Expertise */}
          <div className="max-w-5xl mx-auto">
            <h4 className="text-3xl sm:text-4xl font-display text-foreground text-center mb-8">
              What Makes Us Unique
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {differentiators.map((item, index) => (
                <Card 
                  key={index}
                  className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-card border-border group"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors flex-shrink-0">
                      <item.icon className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h5 className="text-xl font-display mb-2 text-foreground">
                        {item.title}
                      </h5>
                      <p className="text-muted-foreground leading-relaxed text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Final Statement */}
          <div className="max-w-3xl mx-auto text-center mt-10">
            <p className="text-xl sm:text-2xl font-display text-foreground leading-relaxed">
              This is why our projects aren't just buildings—they're 
              <span className="text-primary"> climbing experiences crafted with genuine passion and unmatched expertise</span>.
            </p>
            <p className="text-lg text-muted-foreground mt-6">
              We put our heart into every project because we're not just builders, we're climbers. When you work with Falkors, you're partnering with people who understand climbing at the deepest level—<span className="text-foreground font-semibold">because we've lived it.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompetitiveEdge;
